import React, { FC, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { classNames } from 'utils';
import { useMgmtFeeContext } from 'context/MgmtFeeContext';
import {
  AccountOverviewIcon,
  ChevronRight,
  HelpCenterIcon,
  ProfileIcon,
  SettingsIcon,
  PaymnetIcon,
} from '../../../assets/icons';
import { AppContext } from '../../../context/ContextProvider';
import { useImp } from '../../../hooks/useImp';
import { AccountViewType, SubjectOptionKeys } from '../types';

const accountMenuOptions = [
  {
    id: AccountViewType.OVERVIEW,
    titleKey: 'overview',
    icon: () => <AccountOverviewIcon fill="currentColor" />,
  },
  {
    id: AccountViewType.PROFILE,
    titleKey: 'profile',
    icon: () => <ProfileIcon fill="currentColor" />,
  },
  {
    id: AccountViewType.PAYMENTS,
    titleKey: 'payments',
    icon: () => <PaymnetIcon fill="currentColor" />,
  },
  {
    id: AccountViewType.SETTINGS,
    titleKey: 'settings',
    icon: () => <SettingsIcon fill="currentColor" />,
  },
  {
    id: AccountViewType.HELP_CENTER,
    titleKey: 'help-center',
    icon: () => <HelpCenterIcon fill="currentColor" />,
  },
  {
    id: AccountViewType.IMPERSONATION,
    titleKey: 'impersonation',
    icon: () => <ProfileIcon fill="currentColor" />,
  },
];

interface AccSideMenuProps {
  selectedView: AccountViewType;
  setSelectedView: (id: AccountViewType, subject?: SubjectOptionKeys) => void;
  containerClassName?: string;
}

const AccSideMenu: FC<AccSideMenuProps> = ({ setSelectedView, selectedView, containerClassName }) => {
  const { t } = useTranslation();
  const { cardManagement14583, impFlag } = useFlags();
  const {
    state: { isLockedOut },
  } = useMgmtFeeContext();
  const {
    state: {
      settings: { email: rmEmail },
    },
  } = useContext(AppContext);
  const { impInfo } = useImp(rmEmail);
  // TEMP DEBUG — remove once impersonation menu visibility is confirmed
  // eslint-disable-next-line no-console
  console.log('[impersonation gate]', { impFlag, rmEmail, impInfo });
  // const { results: rmDetails, loading: loadingRmDetails } = useExecuteQuery('getCustomerRM', GET_RM_DETAILS);
  // const displayText = useMemo(() => buildDisplayText(Object.values(DisplayTextKeys), 'account:sideMenu', t), [t]);
  const cClassname = containerClassName && containerClassName.length > 0 ? containerClassName : `hidden sm:flex w-fit`;
  const accountsMenu = useMemo(() => {
    return accountMenuOptions
      .filter((opt) => {
        if (
          isLockedOut &&
          [AccountViewType.PAYMENTS, AccountViewType.SETTINGS, AccountViewType.PROFILE].includes(opt.id)
        ) {
          return false;
        }
        return true;
      })
      .filter((option) => {
        if (option.id === AccountViewType.PAYMENTS && !cardManagement14583) {
          return false;
        }

        // Impersonation is gated by a feature flag AND the RM having permission
        if (option.id === AccountViewType.IMPERSONATION && !(impFlag && impInfo.hasPermission)) {
          return false;
        }

        return true;
      })
      .map((x) => ({ ...x, title: t(`account:titles.${x.titleKey}`) }));
  }, [t, cardManagement14583, isLockedOut, impFlag, impInfo.hasPermission]);
  // const rmInfo = useMemo(() => {
  //   if (loadingRmDetails) return null;
  //   const rm = rmDetails as RMDetails;
  //   return { title: '', name: rm.name, photoUrl: rm.photo };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [rmDetails]);
  return (
    <div
      className={`flex-col items-center h-full min-w-[263px] bg-white dark:bg-slate-800 border-r border-gray-200 dark:border-slate-700 ${cClassname}`}
    >
      <div className="flex-1 w-full py-2">
        {accountsMenu.map((menuItem) => {
          const isSelected = selectedView === menuItem.id;
          return (
            <div
              key={`${menuItem.id}`}
              onClick={() => setSelectedView(menuItem.id)}
              className={classNames(
                'cursor-pointer flex items-center mx-2 px-4 py-3 rounded-xl transition-all duration-200',
                'text-gray-600 dark:text-gray-300',
                'hover:bg-gray-100 dark:hover:bg-slate-700 hover:text-gray-900 dark:hover:text-white',
                isSelected ? 'bg-orange/10 dark:bg-teal-500/10 text-orange dark:text-teal-400 font-medium' : '',
              )}
            >
              <span className={isSelected ? 'text-orange dark:text-teal-400' : ''}>{menuItem.icon()}</span>
              <div className="text-sm flex-1 ml-3">{menuItem.title}</div>
              <ChevronRight
                className={`w-4 h-4 ${
                  isSelected ? 'text-orange dark:text-teal-400' : 'text-gray-400 dark:text-gray-500'
                }`}
              />
            </div>
          );
        })}
      </div>
      {/* Commentting this for now, awaiting business decision */}
      {/* <div className="flex bg-gray-100 w-[263px] p-1 rounded-lg items-center m-2 mt-5 border border-gray-300">
        {loadingRmDetails && (
          <div className="flex w-full h-full justify-center items-center">
            <Loading />
          </div>
        )}
        {!loadingRmDetails && (
          <>
            <img className="w-[97px] h-[104px] object-cover rounded-lg rounded-r-none" src={rmInfo?.photoUrl} alt="" />
            <div className="flex flex-1 flex-col items-center justify-center  ">
              <span className="text-base">{rmInfo?.name}</span>
              <span className="text-xs">{rmInfo?.title}</span>
              <Button
                className={`btn text-14 mt-3 font-normal rounded-lg border border-black  text-black`}
                onClick={() => setSelectedView(AccountViewType.CONTACT_US, SubjectOptionKeys.RELATION_MGR)}
                props={{
                  name: 'viewPortfolio',
                }}
              >
                {displayText[DisplayTextKeys.CONTACT_RM]}
              </Button>
            </div>
          </>
        )}
      </div> */}
    </div>
  );
};

export default AccSideMenu;
